<template>
  <aside
    :class="[
      'sidebar h-full overflow-y-auto transition-[width] motion-reduce:transition-none duration-300 ease-in-out flex flex-col bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800',
      isCollapsed ? 'w-16' : 'w-60'
    ]"
  >
    <!-- Toggle -->
    <div class="flex items-center px-3 py-3 border-b border-neutral-100 dark:border-neutral-800" :class="isCollapsed ? 'justify-center' : 'justify-end'">
      <button
        @click="toggleCollapse"
        class="p-1.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-neutral-500 dark:text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
        :title="isCollapsed ? 'Expand' : 'Collapse'"
        :aria-label="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        :aria-expanded="!isCollapsed"
      >
        <span class="material-icons text-lg" aria-hidden="true">
          {{ isCollapsed ? 'chevron_right' : 'chevron_left' }}
        </span>
      </button>
    </div>

    <nav class="flex-1 py-3 space-y-5">
      <!-- Operations -->
      <!-- "Zone Planning" was merged into Settings > Zones (ZonePlanning.vue is a
           redirect) — a duplicate item here double-highlighted with Settings > Zones. -->
      <NavSection :collapsed="isCollapsed" label="Operations">
        <NavItem :collapsed="isCollapsed" icon="shopping_cart" label="Orders" route="Orders" :active="activeRoute === 'Orders'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="calendar_today" label="Planning" route="Planning" :active="activeRoute === 'Planning'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="auto_awesome" label="Suggestions" route="Suggestions" :active="activeRoute === 'Suggestions'" :badge="suggestionsBadge" @navigate="handleRouting" />
      </NavSection>

      <!-- Analytics -->
      <NavSection :collapsed="isCollapsed" label="Analytics">
        <NavItem :collapsed="isCollapsed" icon="bar_chart" label="Dashboard" route="AnalyticsDashboard" :active="activeRoute === 'AnalyticsDashboard'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="group_work" label="Supplier Groups" route="SupplierGroups" :active="activeRoute === 'SupplierGroups'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="description" label="Reports" route="AnalyticsReports" :active="activeRoute === 'AnalyticsReports'" @navigate="handleRouting" />
      </NavSection>

      <!-- HomeBase -->
      <NavSection :collapsed="isCollapsed" label="HomeBase">
        <NavItem :collapsed="isCollapsed" icon="person_add" label="Signups" route="HomeBaseSignups" :active="activeRoute === 'HomeBaseSignups'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="local_shipping" label="Deployments" route="HomeBaseDeployments" :active="activeRoute === 'HomeBaseDeployments'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="groups" label="Households" route="HomeBaseHouseHolds" :active="activeRoute === 'HomeBaseHouseHolds'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="inventory_2" label="Containers" route="HomeBaseContainers" :active="activeRoute === 'HomeBaseContainers'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="assignment" label="Collection Requests" route="HomeBaseCollectionRequests" :active="activeRoute === 'HomeBaseCollectionRequests'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="receipt_long" label="Collection Receipts" route="HomeBaseCollectionReceipts" :active="activeRoute === 'HomeBaseCollectionReceipts'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="people" label="Users" route="HomeBaseUsers" :active="activeRoute === 'HomeBaseUsers'" @navigate="handleRouting" />
      </NavSection>

      <!-- Administration -->
      <NavSection :collapsed="isCollapsed" label="Administration">
        <NavItem :collapsed="isCollapsed" icon="store" label="Suppliers" route="Suppliers" :active="activeRoute === 'Suppliers'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="sync_alt" label="Odoo Sync" route="OdooContacts" :active="activeRoute === 'OdooContacts'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="badge" label="Drivers" route="Drivers" :active="activeRoute === 'Drivers'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="local_shipping" label="Trucks" route="Trucks" :active="activeRoute === 'Trucks'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="warehouse" label="Inventory" route="Inventory" :active="activeRoute === 'Inventory'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="qr_code" label="Code Generator" route="CodeGenerator" :active="activeRoute === 'CodeGenerator'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="assessment" label="Reports" route="Reports" :active="activeRoute === 'Reports'" @navigate="handleRouting" />
      </NavSection>

      <!-- Settings -->
      <NavSection :collapsed="isCollapsed" label="Settings">
        <NavItem :collapsed="isCollapsed" icon="business" label="Sites" route="Sites" :active="activeRoute === 'Sites'" :disabled="true" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="category" label="Container Types" route="Containers" :active="activeRoute === 'Containers'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="map" label="Zones" route="Zones" :active="activeRoute === 'Zones'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="people" label="Users" route="Users" :active="activeRoute === 'Users'" @navigate="handleRouting" />
        <NavItem :collapsed="isCollapsed" icon="notifications" label="Notifications" route="Notifications" :active="activeRoute === 'Notifications'" @navigate="handleRouting" />
      </NavSection>
    </nav>
  </aside>
</template>

<script>
import { ref, computed, onMounted, watch, h } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useStore } from "vuex";
import useSuggestionsSummary from "@/composables/useSuggestionsSummary";

// Section wrapper
const NavSection = {
  name: "NavSection",
  props: { collapsed: Boolean, label: String },
  setup(props, { slots }) {
    return () =>
      h("div", {}, [
        props.collapsed
          ? h("div", { class: "nav-divider mx-3 mb-2" })
          : h("p", { class: "px-4 mb-1.5 text-[10px] font-bold uppercase tracking-widest text-neutral-500 dark:text-neutral-400" }, props.label),
        h("div", { class: "space-y-0.5 px-2" }, slots.default?.()),
      ]);
  },
};

// Individual nav item
const NavItem = {
  name: "NavItem",
  props: {
    collapsed: Boolean,
    icon: String,
    label: String,
    route: String,
    active: Boolean,
    disabled: Boolean,
    badge: { type: [Number, String], default: 0 },
  },
  emits: ["navigate"],
  setup(props, { emit }) {
    const router = useRouter();

    const handleClick = (e) => {
      // Preserve native link behavior (new tab, copy link) for modified clicks.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      if (!props.disabled) emit("navigate", props.route);
    };

    return () => {
      const itemClass = [
        "nav-item w-full flex items-center rounded-lg text-sm transition-all duration-150 relative",
        props.collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2",
        props.disabled && "opacity-40 cursor-not-allowed",
        props.active
          ? "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 font-semibold"
          : "text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-neutral-200",
      ]
        .filter(Boolean)
        .join(" ");

      const content = [
        // Active indicator bar
        props.active &&
          h("span", {
            "aria-hidden": "true",
            class: "absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-4 rounded-r-full bg-primary-600 dark:bg-primary-400",
          }),
        h("span", { class: "material-icons text-[20px] flex-shrink-0", "aria-hidden": "true" }, props.icon),
        !props.collapsed &&
          h("span", { class: "truncate text-left" }, props.label),
        // Count badge (expanded) / dot (collapsed) — e.g. new suggestions overnight
        !props.collapsed && props.badge
          ? h("span", {
              class: "ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-primary-600 text-white text-[11px] font-bold flex items-center justify-center",
              "aria-label": `${props.badge} new`,
            }, String(props.badge))
          : null,
        props.collapsed && props.badge
          ? h("span", { "aria-hidden": "true", class: "absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-primary-600" })
          : null,
      ];

      // Disabled items stay buttons (anchors cannot be disabled).
      if (props.disabled) {
        return h(
          "button",
          {
            disabled: true,
            "aria-disabled": "true",
            title: `${props.label} — coming soon`,
            "aria-label": props.collapsed ? props.label : null,
            class: itemClass,
          },
          content
        );
      }

      // Real link: middle-click/cmd-click open in new tab, SR users hear a link.
      return h(
        "a",
        {
          href: router.resolve({ name: props.route }).href,
          onClick: handleClick,
          title: props.collapsed ? props.label : null,
          "aria-label": props.collapsed ? props.label : null,
          "aria-current": props.active ? "page" : null,
          class: itemClass,
        },
        content
      );
    };
  },
};

export default {
  components: { NavSection, NavItem },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const isCollapsed = ref(false);

    // Active state is derived from the actual route (fixes refresh, deep
    // links and back/forward). Detail routes highlight their parent item.
    const ROUTE_PARENTS = {
      OrderView: "Orders",
      OrderPdfView: "Orders",
      RouteView: "Planning",
      ZonePlanning: "Zones",
      SupplierView: "Suppliers",
      LocationView: "Suppliers",
      AnalyticsSupplierView: "AnalyticsDashboard",
      SupplierGroupAnalytics: "SupplierGroups",
      ZoneView: "Zones",
      UserView: "Users",
    };
    const activeRoute = computed(() => ROUTE_PARENTS[route.name] || route.name);

    const { badgeCount: suggestionsBadge, start: startSuggestionsSummary, refresh: refreshSuggestionsSummary } = useSuggestionsSummary(store);

    onMounted(() => {
      const savedState = localStorage.getItem("sideNavbarCollapsed");
      if (savedState !== null) {
        isCollapsed.value = savedState === "true";
      }
      startSuggestionsSummary();
    });

    watch(() => store.state.currentSite, refreshSuggestionsSummary);

    watch(isCollapsed, (newValue) => {
      localStorage.setItem("sideNavbarCollapsed", newValue.toString());
    });

    const toggleCollapse = () => {
      isCollapsed.value = !isCollapsed.value;
    };

    const handleRouting = (routeName) => {
      router.push({ name: routeName });
      // Kept for views that still read store.state.currentRoute; the sidebar
      // highlight itself no longer depends on it.
      store.commit("setCurrentRoute", routeName);
    };

    return {
      handleRouting,
      activeRoute,
      isCollapsed,
      toggleCollapse,
      suggestionsBadge,
    };
  },
};
</script>

<style scoped>
.nav-divider {
  height: 1px;
  background: linear-gradient(to right, transparent, var(--color-neutral-200), transparent);
}
:root.dark .nav-divider {
  background: linear-gradient(to right, transparent, var(--color-neutral-700), transparent);
}

.nav-item:active:not(:disabled) {
  transform: scale(0.98);
}

/* Scrollbar */
.sidebar::-webkit-scrollbar {
  width: 4px;
}
.sidebar::-webkit-scrollbar-track {
  background: transparent;
}
.sidebar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.08);
  border-radius: 2px;
}
.sidebar::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.15);
}
:root.dark .sidebar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
}
:root.dark .sidebar::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}
</style>
